import { Minus, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { cn } from "@/lib/utils";
import { collapse, type DiffLine, lineDiff } from "./diff.ts";

/** Added and removed lines against the base version, marked by icon and words as well as tint. */
export function DiffView(props: {
  before: string;
  after: string;
  version: number;
  /** The heading instead of "Changes against version N" (another base than a version). */
  label?: string;
  /** What an empty diff says (the draft review's wording by default). */
  noChanges?: string;
  /** The too-large notice and the two sides' names (the draft review's wording by default). */
  tooLarge?: { notice: string; before: string; after: string };
}) {
  const { t } = useTranslation();
  const diff = useMemo(() => lineDiff(props.before, props.after), [props.before, props.after]);
  const [shown, setShown] = useState<ReadonlySet<number>>(new Set());
  if (!diff) {
    return (
      <div className="flex flex-col gap-4">
        <Notice>{props.tooLarge?.notice ?? t("wiki.draft.tooLarge")}</Notice>
        <div className="rounded-lg border border-line bg-surface-raised p-5">
          <details>
            <summary>{props.tooLarge?.before ?? t("wiki.changes.before")}</summary>
            <pre className="whitespace-pre-wrap break-words">{props.before}</pre>
          </details>
          <details open>
            <summary>{props.tooLarge?.after ?? t("wiki.changes.after")}</summary>
            <pre className="whitespace-pre-wrap break-words">{props.after}</pre>
          </details>
        </div>
      </div>
    );
  }
  if (diff.every((l) => l.kind === "same")) {
    return (
      <p className="text-sm text-fg-secondary">{props.noChanges ?? t("wiki.draft.noChanges")}</p>
    );
  }
  const line = (l: DiffLine, key: string) => (
    <li
      key={key}
      className={cn(
        "flex gap-2 px-3 py-px",
        l.kind === "add" && "bg-success-bg",
        l.kind === "del" && "bg-danger-bg",
      )}
    >
      <span aria-hidden="true" className="flex w-3.5 shrink-0 items-center">
        {l.kind === "add" ? (
          <Plus className="size-3.5 text-success-fg" />
        ) : l.kind === "del" ? (
          <Minus className="size-3.5 text-danger-fg" />
        ) : null}
      </span>
      <span className="min-w-0 break-words whitespace-pre-wrap text-fg">
        {l.kind === "same" ? null : (
          <span className="sr-only">
            {t(l.kind === "add" ? "wiki.draft.added" : "wiki.draft.removed")}:{" "}
          </span>
        )}
        {l.text || " "}
      </span>
    </li>
  );
  return (
    <section aria-label={props.label ?? t("wiki.draft.changesAgainst", { version: props.version })}>
      <h3 className="mb-2 text-xs font-medium text-fg-muted">
        {props.label ?? t("wiki.draft.changesAgainst", { version: props.version })}
      </h3>
      <ol className="overflow-x-auto rounded-lg border border-line bg-surface-raised py-1 font-mono text-[13px] leading-relaxed">
        {collapse(diff).map((row, i) =>
          row.kind === "skip" ? (
            shown.has(i) ? (
              row.lines.map((l, j) => line(l, `${i}-${j}`))
            ) : (
              // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional in a fixed diff
              <li key={i}>
                <button
                  type="button"
                  className="w-full px-3 py-1 text-left font-sans text-xs text-fg-muted hover:bg-surface-hover"
                  onClick={() => setShown(new Set([...shown, i]))}
                >
                  ⋯ {t("wiki.draft.unchanged", { count: row.lines.length })}
                </button>
              </li>
            )
          ) : (
            line(row, `${i}`)
          ),
        )}
      </ol>
    </section>
  );
}
